import React from "react";
import { ScrollView } from "react-native";
import { WidgetSizeT } from "../../../localTypes/WidgetSizeT";
import { WidgetTodoOverviewPreview } from "widgets/src/WidgetTodoOverview/WidgetTodoOverview";
import { graphql, useFragment, useLazyLoadQuery } from "react-relay";
import { WACategoryTodoCategorySelectTypeComponent_widget$key } from "./__generated__/WACategoryTodoCategorySelectTypeComponent_widget.graphql";
import { WACategoryWidgetContainer } from "../../WACategoryWidgetContainer";
import { getWidgetAddWidgetSizeWithinBounds } from "../../utils/useWidgetAddWidgetSizeState";
import { overviewWidgetSizes } from "../../utils/widgetSizes/overviewWidgetSizes";
import { LazyQueryNetworkBoundrary } from "../../../../../../components/mobile/QueryNetworkBoundrary";
import { WACategoryTodoCategorySelectTypeComponentQuery } from "./__generated__/WACategoryTodoCategorySelectTypeComponentQuery.graphql";
import { useWACategoryLayout } from "../../utils/useWACategoryLayout";
import { FormattedMessage } from "react-intl";
import { WACategoryItemSeparator } from "../../WACategoryItemSeparator";
import { WidgetTodoSummary } from "widgets/src/WIdgetTodoSummary/WidgetTodoSummary";
import { WidgetTodoSummary_widgetTodoSummary$key } from "widgets/src/WIdgetTodoSummary/__generated__/WidgetTodoSummary_widgetTodoSummary.graphql";

type P = {
  unitSize: number;
  maxSize: WidgetSizeT;
  storecastId: string;
  categoryId: string;
  onAdd: (type: "WidgetTodoSummary" | "WidgetTodoOverview") => void;
};

export function WACategoryTodoCategorySelectTypeComponent(props: P) {
  return (
    <LazyQueryNetworkBoundrary>
      {(fetchKey) => (
        <WACategoryTodoCategorySelectTypeComponent_
          {...props}
          fetchKey={fetchKey}
        />
      )}
    </LazyQueryNetworkBoundrary>
  );
}

function WACategoryTodoCategorySelectTypeComponent_(
  props: P & {
    fetchKey: string;
  }
) {
  const data = useLazyLoadQuery<WACategoryTodoCategorySelectTypeComponentQuery>(
    graphql`
      query WACategoryTodoCategorySelectTypeComponentQuery(
        $storecastId: ID!
        $input: PreviewWidgetTodoCategoryOverviewInput!
        $summaryInput: PreviewWidgetTodoSummaryInput!
        $first: Int
        $after: String
      ) {
        dashboard(id: $storecastId) @required(action: THROW) {
          previewWidgetTodoCategoryOverview(input: $input) {
            ...WACategoryTodoCategorySelectTypeComponent_widget
          }
          previewWidgetTodoSummary(input: $summaryInput) {
            ...WidgetTodoSummary_widgetTodoSummary
          }
        }
      }
    `,
    {
      input: {
        categoryId: props.categoryId,
        size: "TWO_X_TWO",
        displayMode: "MEDIUM",
        storecastId: props.storecastId,
        teamIds: null,
      },
      summaryInput: {
        size: "TWO_X_TWO",
        storecastId: props.storecastId,
        teamIds: null,
        todoCategoryId: props.categoryId,
      },
      storecastId: props.storecastId,
    },
    {}
  );
  return data.dashboard.previewWidgetTodoCategoryOverview ? (
    <WACategoryTodoCategorySelectTypeComponent__
      {...props}
      overviewWidget={data.dashboard.previewWidgetTodoCategoryOverview}
      summaryWidget={data.dashboard.previewWidgetTodoSummary}
      onAdd={props.onAdd}
    />
  ) : null;
}

function WACategoryTodoCategorySelectTypeComponent__(
  props: P & {
    overviewWidget: WACategoryTodoCategorySelectTypeComponent_widget$key;
    summaryWidget: WidgetTodoSummary_widgetTodoSummary$key;
    onAdd: (type: "WidgetTodoSummary" | "WidgetTodoOverview") => void;
    fetchKey: string;
  }
) {
  const overviewWidget = useFragment(
    graphql`
      fragment WACategoryTodoCategorySelectTypeComponent_widget on WidgetTodoOverview {
        ...WidgetTodoOverviewPreview_widget
        title
        category @required(action: THROW) {
          id
          title
          isDefault
        }
      }
    `,
    props.overviewWidget
  );
  const [size, hasSizeWithinBounds] = getWidgetAddWidgetSizeWithinBounds({
    widgetSizes: overviewWidgetSizes,
    maxSize: props.maxSize,
  });
  const contentContainerStyle = useWACategoryLayout();

  return (
    <ScrollView
      contentContainerStyle={contentContainerStyle}
      style={{ flex: 1 }}
    >
      <WACategoryWidgetContainer
        hasSizeWithinBounds={hasSizeWithinBounds}
        unitSize={props.unitSize}
        size={size}
        title={
          !overviewWidget.category.isDefault ? (
            <FormattedMessage
              defaultMessage="{categoryName} List"
              description="WACategoryTodoCategorySelectTypeComponent Custom List"
              values={{ categoryName: overviewWidget.category.title }}
            />
          ) : (
            <FormattedMessage
              defaultMessage="To-Do List"
              description="WACategoryTodoCategorySelectTypeComponent To-Do List"
            />
          )
        }
        description={
          <FormattedMessage
            defaultMessage="Customise the list by choosing list style and filtering teams."
            description="WACategoryTodoCategorySelectTypeComponent list description"
          />
        }
        onAdd={() => props.onAdd("WidgetTodoOverview")}
      >
        <WidgetTodoOverviewPreview
          widget={overviewWidget}
          mode="IDLE"
          interactive={false}
          fetchKey={props.fetchKey}
          storecastId={props.storecastId}
        />
      </WACategoryWidgetContainer>
      <WACategoryItemSeparator />
      <WACategoryWidgetContainer
        hasSizeWithinBounds={hasSizeWithinBounds}
        unitSize={props.unitSize}
        size={size}
        title={
          !overviewWidget.category.isDefault ? (
            <FormattedMessage
              defaultMessage="{categoryName} - Teams"
              description="WACategoryTodoCategorySelectTypeComponent Custom List - Teams Summary"
              values={{ categoryName: overviewWidget.category.title }}
            />
          ) : (
            <FormattedMessage
              defaultMessage="To-Do's - Teams"
              description="WACategoryTodoCategorySelectTypeComponent To-Do's - Teams"
            />
          )
        }
        description={
          <FormattedMessage
            defaultMessage="Customise the list by filtering teams."
            description="WACategoryTodoCategorySelectTypeComponent To-Do's - Teams description"
          />
        }
        onAdd={() => props.onAdd("WidgetTodoSummary")}
      >
        <WidgetTodoSummary
          widget={props.summaryWidget}
          mode="IDLE"
          storecastId={props.storecastId}
        />
      </WACategoryWidgetContainer>
    </ScrollView>
  );
}
